/**
 * The administrator's session.
 *
 * The same accounts and the same sign-in as the job seeker app; what differs
 * is the door: only an account whose role is `admin` is let in, and the server
 * checks that again on every request (`/admin/*` answers 403 otherwise).
 * Stored under its own key, so being signed in to the job seeker app in the
 * same browser neither signs you in here nor is disturbed by signing out here.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { ApiError, configureClient } from '@/api/client'
import { authApi } from '@/api/endpoints'
import type { AdminMe, TokenPair } from '@/types/api'

interface AuthState {
  admin: AdminMe | null
  tokens: TokenPair | null
  status: 'idle' | 'authenticating' | 'authenticated' | 'anonymous'
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  hydrate: () => Promise<void>
  clear: () => void
}

/** Thrown by `login` for a correct password on an account that is not an administrator. */
export class NotAdminError extends Error {
  constructor() {
    super('This account is not an administrator.')
    this.name = 'NotAdminError'
  }
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      admin: null,
      tokens: null,
      status: 'idle',

      clear: () => set({ admin: null, tokens: null, status: 'anonymous' }),

      login: async (email, password) => {
        set({ status: 'authenticating' })
        try {
          const session = await authApi.login({ email, password })
          if (session.user.role !== 'admin') {
            // A real session was just opened for a job seeker: close it again.
            set({ tokens: session.tokens })
            await authApi.logout(session.tokens.refreshToken).catch(() => undefined)
            throw new NotAdminError()
          }
          const { id, fullName, role } = session.user
          set({ admin: { id, email: session.user.email, fullName, role }, tokens: session.tokens, status: 'authenticated' })
        } catch (error) {
          set({ admin: null, tokens: null, status: 'anonymous' })
          throw error
        }
      },

      logout: async () => {
        const refreshToken = get().tokens?.refreshToken
        // Best effort: a failed revoke must not trap anyone in a session.
        if (refreshToken) await authApi.logout(refreshToken).catch(() => undefined)
        get().clear()
      },

      hydrate: async () => {
        if (!get().tokens?.accessToken) {
          set({ status: 'anonymous' })
          return
        }
        try {
          set({ admin: await authApi.me(), status: 'authenticated' })
        } catch (error) {
          // Offline or a server hiccup: keep the session and let the pages retry.
          if (error instanceof ApiError && error.status !== 401 && error.status !== 403) {
            set({ status: get().admin ? 'authenticated' : 'anonymous' })
            return
          }
          get().clear()
        }
      },
    }),
    {
      name: 'job-seeker-admin-auth',
      partialize: (state) => ({ admin: state.admin, tokens: state.tokens }),
    },
  ),
)

/** Wire the HTTP client to this store. Called once, at startup. */
export function initAuthBridge(): void {
  let inFlightRefresh: Promise<string | null> | null = null

  configureClient({
    getAccessToken: () => useAuthStore.getState().tokens?.accessToken ?? null,

    refreshTokens: async () => {
      // Collapse concurrent 401s into one refresh: the polls here fire together.
      if (inFlightRefresh) return inFlightRefresh
      const refreshToken = useAuthStore.getState().tokens?.refreshToken
      if (!refreshToken) return null
      inFlightRefresh = (async () => {
        try {
          const tokens = await authApi.refresh(refreshToken)
          useAuthStore.setState({ tokens })
          return tokens.accessToken
        } catch {
          useAuthStore.getState().clear()
          return null
        } finally {
          inFlightRefresh = null
        }
      })()
      return inFlightRefresh
    },

    onUnauthorized: () => useAuthStore.getState().clear(),
  })
}
